import { DatePicker, Select} from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { getHomeworkActivitySummary, getHomeworkStudents, getHomeworkSummary } from "../../api/statistics.api";
import Button from "../../components/Button";
import ErrorResponse from "../../components/ErrorResponse";
import SimpleHeader from "../../components/layout/SimpleHeader";
import Loader from "../../components/Loader";
import SelectLevel from "../../components/statistics/e-learning/SelectLevel";
import List from "../../components/statistics/homework/List";
import Summary from "../../components/statistics/homework/Summary";
import MultiCampusModal from "../../components/statistics/regular-test/modals/MultiCampusModal";
import TabItem from "../../components/statistics/TabItem";
import useAxios from "../../hooks/useAxios";
import useNotification from "../../hooks/useNotification";
import config from "../../lib/config";
import { IGetHomeworkActivitySummary, IGetHomeworkStudents, IGetHomeworkSummary } from "../../models/statistics.model";
import { RootState } from "../../store/rootReducer";
import Activity from "../../components/statistics/homework/Activity";
import { getSemesterMonths, getSemesters } from "../../api/semesters.api";
import { IGetSemesterMonths, IGetSemesters } from "../../models/semesters.model";
import EduStepSelect from "../../components/common/EduStepSelect";
import TableEmpty from "../../components/common/TableEmpty";
import SelectCampusMultiChoice from "../../components/SelectCampusMultiChoice";

enum Contents {
  Summary,
  Activity,  
  List,
}

const StatisticsHomework = () => {
  const { t } = useTranslation();
  const { auth } = useSelector((state: RootState) => state.auth);
  const { infoNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetHomeworkSummary>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetHomeworkStudents>();
  const { data: res3, isLoading: isLoading3, error: error3, execute: execute3 } = useAxios<IGetHomeworkActivitySummary>();
  const { data: res4, isLoading: isLoading4, error: error4, execute: execute4 } = useAxios<IGetSemesters>();
  const { data: resMonth, execute: executeM } = useAxios<IGetSemesterMonths>();

  const [content, setContent] = useState(Contents.Summary);
  const [semesterCode, setSemesterCode] = useState<number>();
  const [eduStepCode, setEduStepCode] = useState<string>();
  const [multiCampusVisible, setMultiCampusVisible] = useState(false);
  const [campusCodes, setCampusCodes] = useState<string[]>([]);
  const [courseCode, setCourseCode] = useState<string>();
  const [srtDate, setSrtDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isFirstRun, setIsFirstRun] = useState(true);
  const [pageNo, setPageNo] = useState(1);

  useEffect(() => {
    execute4(getSemesters());

    defSelectBoxSetting();
  }, []);

  const defSelectBoxSetting = () => {
    //검색 필드 Default값 셋팅
    const defSelValue:any = "";
    setEduStepCode(defSelValue);
    setCourseCode(defSelValue);
  }

  useEffect(() => {
    if (isFirstRun) {
      setIsFirstRun(false);
      return;
    }
    if (content === Contents.List && !courseCode) {
      setCourseCode(undefined);
    }

    getHomeworkData(1);
  }, [content]);

  useEffect(() => {
    if (isFirstRun) {
      return;
    }

    getHomeworkData();
  }, [pageNo]);

  const getHomeworkData = (_pageNo?: number) => {
    if (!semesterCode) {
      return infoNotification(t('statistics.dttt.message1'));
    }

    if (content === Contents.List && !courseCode) {
      return infoNotification(t('statistics.eLearning.2'));
    }

    const req: any = {
      semesterCode,
      courseCode,
      campusCode: auth.hqYn === 'Y'
        ? campusCodes
        : (campusCodes.length ? campusCodes : auth.campus)
    };

    switch (content) {
      case Contents.Summary:
        if (semesterCode) {
          executeM(getSemesterMonths(String(semesterCode)));
        }
        execute(getHomeworkSummary(req));
        break;
      case Contents.List:
        req.perPage = config.pageSize;
        req.pageNo = _pageNo || pageNo;
        execute2(getHomeworkStudents(req));
        break;
      case Contents.Activity:
        execute3(getHomeworkActivitySummary(req));
        break;
    }
  }

  const renderContent = () => {
    switch (content) {
      case Contents.Summary:
        return <Summary rawData={res?.data} rawData2={resMonth?.data} />;
      case Contents.List:
        return (
          <List
            res={res2}
            pageNo={pageNo}
            setPageNo={setPageNo}
            srtDate={srtDate}
            endDate={endDate}
            campusCodes={campusCodes}
            courseCode={courseCode}
            semesterCode={semesterCode}
          />
        );
      case Contents.Activity:
        return (
          <Activity res={res3?.data}></Activity>
        )
    }
  }

  return (
    <>
    <SimpleHeader
      iconFile="icon-statistics-big.svg"
      parentMenu={t('common.menu.statistics')}
      currentMenu={t('statistics.homework.title1')}
      onlyTitle={true}
    />
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('common.menu.campus')}</label>  {/* 캠퍼스 */}
            <SelectCampusMultiChoice
              placeholder={t('statistics.eLearning.title2')}
              onChange={setCampusCodes}
              value={campusCodes}
            />
            <label>
              ({campusCodes.length ? `${campusCodes.length}${t('statistics.attendance.text1')}` : t('common.text.all')})
            </label>
          </li>
          <li>
            <label>{t('class.class.placeholder4')}</label> {/* 학기 */}
            <Select
              placeholder={t('statistics.regularTest.placeholder2')}
              value={semesterCode}
              onChange={value => setSemesterCode(value)}
            >
              {res4?.data?.filter(item => item.semesterType === 'RG').map(item => (
                <Select.Option
                  key={item.semesterCode}
                  value={item.semesterCode}
                >
                  {item.semesterName}
                </Select.Option>
              ))}
            </Select>
          </li>
          <li>
            <label>{t('common.placeholder.1')}</label> {/* 교육단계 */}
            <EduStepSelect
              eduStepCode={eduStepCode}
              setEduStepCode={value => {
                setEduStepCode(value);
                
                if (content === Contents.List) {
                  setCourseCode(undefined);
                } else {
                  const defSelValue:any = "";
                  setCourseCode(defSelValue);
                }
              }}
              width={90}
              includeAll
            />
          </li>
          <li>
            <label>{t('student.student.column26')}</label> {/* 레벨 */}
            <div>  
              <SelectLevel
                courseCode={courseCode}
                setCourseCode={setCourseCode}
                includeAll={content === Contents.Summary || content === Contents.Activity}
                eduStepCode={eduStepCode}
                placeholder={t('statistics.regularTest.placeholder2')}                   
              />
            </div>
            <div className="comm-btn__area">
              <Button
                className="btn-basic small black"
                onClick={() => getHomeworkData(1)}
                text={t('common.button.search')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>

    <div className="content scrollbar">
      {(isLoading || isLoading2 || isLoading3) && <Loader />}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={error3} />
      {
        res?.data || res2?.data || res3?.data
          ? (
            <>
              <div className="comm-tab__line">
                <ul>
                  <TabItem
                    currentContent={content}
                    onSetContent={setContent}
                    tabContent={Contents.Summary}
                    tabLabel={t('statistics.attendance.Tab1')}
                  />
                  <TabItem
                    currentContent={content}
                    onSetContent={setContent}
                    tabContent={Contents.Activity}
                    tabLabel={'Activity'}
                  />
                  <TabItem
                    currentContent={content}
                    onSetContent={setContent}
                    tabContent={Contents.List}
                    tabLabel={t('statistics.homework.tab1')}
                  />
                </ul>
              </div>
              <div>
                {renderContent()}
              </div>
            </>
            )
          : isLoading || isLoading2 || isLoading3
            ? null
            : (
              <TableEmpty text={t('statistics.appday.text1')} />
              )
      }     
      {multiCampusVisible &&
        <MultiCampusModal
          visible={multiCampusVisible}
          setVisible={setMultiCampusVisible}
          campusCodes={campusCodes}
          setCampusCodes={setCampusCodes}
        />
      }      
    </div>
    </>
  );
}

export default StatisticsHomework;
